import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import Slider from "react-slick"
import { message, Tooltip } from "antd"
import "antd/dist/reset.css"
import ProductImageModal from "./ProductImageModal"
import HotDealInfo from "./HotDealInfo"
import { toast } from 'react-toastify';

import "slick-carousel/slick/slick.css"
import "slick-carousel/slick/slick-theme.css"
import QtyControl from "./QtyControl"
import CouponSection from "./CouponSection"
import { addItemToCart } from "api/Cart"
import { fetchVoucherDetail, VoucherItem } from "api/Vourcher"
import { useWishlist } from "hooks/useWishlist"
import { CustomerLike, sendPriceAssessment } from "api/ProductDetail"
import ProductDetailQualityControl from "providers/QualityControlComponent/ProductDetailQualityControl"

export interface ThumbnailRaw {
    id: number | string;
    avatar: string;
    name1?: string;
    alt00?: string;
    order?: number;
}

export interface FlashSaleRaw {
    product_id: number;
    promotion_flash_sale_id: number;
    percent: number;
    promotion_flash_sale?: {
        name: string;
        start_time: string;
        end_time: string;
    } | null;
}

export interface PromotionRaw {
    product_id: number;
    promotion_id: number;
    percent: number;
    promotion?: {
        id: number;
        name: string;
        slug: string;
        start_date: string;
        end_date: string;
        is_active: number;
    } | null;
}

export interface VoucherDetail {
    id: number;
    name: string;
    code: string;
    describe?: string;
    value: number;
    type: number;
    min_amount: number;
    max_discount: number;
    exp_date: string;
    start_date: string;
    is_active: number;
}

export interface VoucherRelation {
    target_id: number;
    voucher_id: number;
    voucher: VoucherDetail | null;
}

export interface ProductRawData {
    id: number;
    code: string;
    name: string;
    slug: string;
    price: number;
    discount_price?: number;
    avatar: string;
    list_thumbnail: string;
    short_content?: string;

    product_category?: {
        id: number;
        name: string;
        slug: string;
        voucher_product?: VoucherRelation[];
    };

    promotion?: {
        type: string;
        percent: number;
        detail?: any;
    } | null;
    promotion_flash_sale_product?: FlashSaleRaw[];
    promotion_product?: PromotionRaw[];
    voucher_product?: VoucherRelation[];
    customer_like?: CustomerLike[];
}

interface ProductGalleryProps {
    product: ProductRawData | null;
    info?: string | { __html: string } | null;
}

export default function ProductGallery({ product, info }: ProductGalleryProps) {
    const sliderRef = useRef<Slider | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [fetchedVouchers, setFetchedVouchers] = useState<VoucherItem[]>([]);
    const SlickSlider = Slider as any;
    const { handleToggleWishlist, isInWishlist } = useWishlist();
    const [assessment, setAssessment] = useState<number>(0);

    const token = localStorage.getItem('token');
    const infoHtml = useMemo(() => {
        if (!info) return "";
        if (typeof info === "string") return info;
        return info.__html || "";
    }, [info]);

    useEffect(() => {
        if (product?.customer_like && product.customer_like.length > 0) {
            // Lấy trạng thái đánh giá gần nhất
            setAssessment(product.customer_like[0].is_like);
        } else {
            setAssessment(0);
        }
    }, [product]);

    // Helper: Format tiền tệ VNĐ
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount).replace('₫', 'đ');
    };

    const handleCopy = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success("Đã sao chép!", {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: true,
            });
        } catch (err) {
            toast.error("Không thể sao chép!", {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: true,
            });
        }
    };

    const onWishlistClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (!product) return;
        handleToggleWishlist(product.id);
    };

    useEffect(() => {
        if (!product) return;

        const loadVouchers = async () => {
            // Lấy tất cả Voucher ID từ sản phẩm và danh mục
            const voucherIds = new Set<number>();

            // Lấy ID từ voucher của sản phẩm
            product.voucher_product?.forEach(item => {
                if (item.voucher_id) voucherIds.add(item.voucher_id);
            });

            // Lấy ID từ voucher của danh mục (nếu có)
            product.product_category?.voucher_product?.forEach(item => {
                if (item.voucher_id) voucherIds.add(item.voucher_id);
            });

            if (voucherIds.size === 0) {
                setFetchedVouchers([]);
                return;
            }

            // Gọi API chi tiết cho từng ID (Sử dụng hàm fetchVoucherDetail bạn mới tạo)
            try {
                const promises = Array.from(voucherIds).map(id => fetchVoucherDetail(id));
                const responses = await Promise.all(promises);

                // Lọc những request thành công và có data
                const validVouchers = responses
                    .filter(res => res.status && res.data)
                    .map(res => res.data as VoucherItem);

                setFetchedVouchers(validVouchers);
            } catch (err) {
                console.error("Lỗi tải voucher:", err);
            }
        };

        loadVouchers();
    }, [product]);

    const handleAssess = async (value: number) => {
        const token = localStorage.getItem('token');
        if (!token) {
            message.warning("Vui lòng đăng nhập để đánh giá!");
            return;
        }

        const newValue = assessment === value ? 0 : value;

        const oldVal = assessment;
        setAssessment(newValue);

        const res = await sendPriceAssessment(product!.id, newValue);

        if (res.status) {
            message.success("Cảm ơn đánh giá của bạn!");
        } else {
            setAssessment(oldVal);
            message.error(res.msg || "Có lỗi xảy ra");
        }
    };

    // ==================== XỬ LÝ DỮ LIỆU (MAPPING) ====================
    const processedData = useMemo(() => {
        if (!product) return null;

        let images: { id: string | number, url: string, alt: string }[] = [];
        try {
            if (product.list_thumbnail && product.list_thumbnail !== '[]') {
                const parsed = JSON.parse(product.list_thumbnail) as ThumbnailRaw[];
                parsed.sort((a, b) => (a.order || 0) - (b.order || 0))
                    .forEach((item) => {
                        images.push({
                            id: item.id,
                            url: item.avatar,
                            alt: item.name1 || item.alt00 || product.name
                        });
                    });
            } else {
                // Fallback nếu list_thumbnail không hợp lệ hoặc rỗng: sử dụng avatar chính
                images.push({
                    id: product.id,
                    url: product.avatar,
                    alt: product.name
                });
            }
        } catch (e) { console.warn("Lỗi parse list_thumbnail", e); }

        // --- 2. Xử lý Giá & Khuyến mãi ---
        let maxDiscount = 0;
        let isFlashSale = false;
        let flashSaleEndTime: Date | null = null;

        // Ưu tiên 0: Nếu có discount_price trực tiếp từ API
        if (product.discount_price && product.discount_price > 0 && product.discount_price < product.price) {
            maxDiscount = Math.round((1 - product.discount_price / product.price) * 100);
            if (product.promotion?.type === 'FLASH_SALE') {
                isFlashSale = true;
            }
        }
        // Ưu tiên 1: Nếu có promotion trực tiếp (promotion.percent)
        else if (product.promotion?.percent && product.promotion.percent > 0) {
            maxDiscount = product.promotion.percent;
            if (product.promotion.type === 'FLASH_SALE') {
                isFlashSale = true;
            }
        }
        // Ưu tiên 2: Kiểm tra Flash Sale từ mảng
        else if (product.promotion_flash_sale_product && product.promotion_flash_sale_product.length > 0) {
            const bestFlashSale = product.promotion_flash_sale_product.reduce((prev, curr) =>
                (prev.percent > curr.percent) ? prev : curr
            );

            maxDiscount = bestFlashSale.percent;
            isFlashSale = true;

            if (bestFlashSale.promotion_flash_sale?.end_time) {
                flashSaleEndTime = new Date(bestFlashSale.promotion_flash_sale.end_time);
            }
        }
        // Ưu tiên 3: Kiểm tra Khuyến mãi thường từ mảng
        else if (product.promotion_product && product.promotion_product.length > 0) {
            maxDiscount = product.promotion_product.reduce((max, item) =>
                (item.percent > max ? item.percent : max), 0
            );
        }

        // Tính toán giá cuối cùng
        const originalPrice = product.price;
        const salePrice = product.discount_price && product.discount_price > 0 && product.discount_price < originalPrice
            ? product.discount_price
            : originalPrice * (1 - maxDiscount / 100);
        const discountAmount = originalPrice - salePrice;

        // --- C. Xử lý Voucher 
        let bestVoucherAmount = 0;
        let bestVoucherCode = "";

        // Duyệt qua danh sách voucher đã fetch từ API
        fetchedVouchers.forEach(v => {
            // Logic tính toán giảm giá (cơ bản)
            // Lưu ý: Cần check thêm min_amount nếu muốn chính xác tuyệt đối
            if (v.min_amount && salePrice < v.min_amount) {
                return; // Bỏ qua nếu chưa đủ đơn tối thiểu
            }

            if (v.value > bestVoucherAmount) {
                bestVoucherAmount = v.value;
                bestVoucherCode = v.code;
            }
        });

        const finalEstPrice = salePrice - bestVoucherAmount;
        const retailPriceSuggestion = originalPrice * 1.2;
        const retailProfit = retailPriceSuggestion - finalEstPrice;

        return {
            images,
           code: product.code,
            name: product.name,
            originalPrice,
            salePrice,
            discountPercent: maxDiscount,
            discountAmount,

            hasVoucher: bestVoucherAmount > 0,
            voucherAmount: bestVoucherAmount,
            voucherCode: bestVoucherCode,
            vouchersList: fetchedVouchers,
            finalEstPrice,

            isFlashSale,
            flashSaleEndTime,
            retailPriceSuggestion,
            retailProfit,
            shortInfo: product.short_content || product.product_category?.name || "Đang cập nhật"
        };
    }, [product, fetchedVouchers]);

    // ==================== STATE QUẢN LÝ SLIDER ====================

    // Lấy mảng ảnh từ dữ liệu đã xử lý
    const galleryImages = processedData?.images || [];

    // State index ảnh đang chọn
    const [selectedIndex, setSelectedIndex] = useState<number>(0);

    // Lấy object ảnh đang active để hiển thị to
    const activeImageObj = galleryImages.length > 0 ? galleryImages[selectedIndex] : null;

    // Reset về ảnh đầu tiên khi đổi sản phẩm
    useEffect(() => {
        if (galleryImages.length > 0) {
            setSelectedIndex(0);
            if (sliderRef.current) {
                sliderRef.current.slickGoTo(0);
            }
        }
    }, [product]);

    // Hàm click thumbnail
    const handleThumbnailClick = (index: number) => {
        setSelectedIndex(index);
    }


    // Lấy mảng ảnh từ dữ liệu đã xử lý
    const totalImages = galleryImages.length;

    const desktopSlidesToShow = Math.max(1, Math.min(3, totalImages || 1));
    const tabletSlidesToShow = Math.max(1, Math.min(3, totalImages || 1));
    const mobileSlidesToShow = Math.max(1, Math.min(3, totalImages || 1));
    const canSlide = totalImages > desktopSlidesToShow;

    // Config Slider
    const sliderSettings = {
        dots: false,
        infinite: canSlide,
        rows: 1,
        slidesPerRow: 1,
        vertical: false,
        verticalSwiping: false,
        speed: 500,
        slidesToShow: desktopSlidesToShow,
        slidesToScroll: 1,
        arrows: false,
        swipeToSlide: true,
        draggable: totalImages > 1,
        swipe: totalImages > 1,
        touchMove: totalImages > 1,
        focusOnSelect: false,
        // slidesToShow: Math.min(galleryImages.length, 3),
        responsive: [
            { breakpoint: 1024, settings: { slidesToShow: tabletSlidesToShow } },
            { breakpoint: 600, settings: { slidesToShow: mobileSlidesToShow } }
        ]
    }

    // ==================== STATE TOOLTIP GIÁ ====================
    const [open, setOpen] = useState(false)
    const [pos, setPos] = useState({ top: 0, left: 0 })
    const priceRef = useRef<HTMLDivElement>(null)

    const handleEnter = () => {
        if (!priceRef.current) return
        const rect = priceRef.current.getBoundingClientRect()
        setPos({ top: rect.top - 10, left: rect.left + rect.width / 2 })
        setOpen(true)
    }

    const handleLeave = () => {
        setOpen(false)
    }

    const sliderInlineStyles = {
        container: {
            marginTop: "12px",
            width: "80%"
        },
        wrapper: {
            display: "flex",
            alignItems: "center",
            gap: "8px"
        },
        arrowButton: {
            width: "32px",
            height: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: "6px",
            backgroundColor: "#f5f5f5",
            flexShrink: 0
        },
        slider: {
            width: "100%"
        },
        sliderViewport: {
            flex: 1,
            minWidth: 0,
            overflow: "hidden"
        },
        slideItem: {
            padding: "0 4px",
            boxSizing: "border-box"
        },
        thumbButton: {
            display: "block",
            width: "100%",
            border: "none",
            background: "transparent",
            cursor: "pointer",
            padding: "2px"
        },
        thumbItem: {
            width: "72px",
            height: "72px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "8px",
            overflow: "hidden",
            border: "1px solid #e5e7eb"
        },
        thumbItemActive: {
            border: "1px solid #1d4ed8"
        },
        thumbImage: {
            width: "100%",
            height: "100%",
            objectFit: "contain",
            display: "block"
        }
    } as const

    if (!product || !processedData) return null;



    return (
        <div className="MuiGrid-root styles_wrapper__Xa5hm MuiGrid-container MuiGrid-spacing-xs-3">

            {/* ================= LEFT ================= */}
            <div className="MuiGrid-root MuiGrid-item MuiGrid-grid-xs-4">
                <div className="styles_bigImage__9eFTb">

                    {/* ===== MAIN IMAGE ===== */}
                    <div className="MuiGrid-root styles_imageShow__0_WTL MuiGrid-item MuiGrid-grid-xs-12">
                        <div className="styles_wrapperImage__Sngdw">
                            <div className="styles_container__boKoW">
                                <div className="styles_image__g98t2 w-full h-full">
                                    {/* <div className="styles_tooltipLeft__UbYbr"><div className="styles_customButton__VYlIZ"><svg className="MuiSvgIcon-root styles_icon__wvMZT" focusable="false" viewBox="0 0 24 24" aria-hidden="true" style={{ fontSize: '24px', color: 'rgba(0, 0, 0, 0.2)' }}><path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"></path></svg></div></div> */}

                                    <div className="styles_tooltipRight__JopJL">
                                        <div
                                            className="styles_customButton__VYlIZ"
                                            onClick={onWishlistClick}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <svg
                                                className="MuiSvgIcon-root styles_icon__wvMZT"
                                                focusable="false"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                                style={{
                                                    fontSize: '24px',
                                                    // Logic đổi màu: Đỏ nếu đã like, Xám mờ nếu chưa
                                                    color: (product && isInWishlist(product.id)) ? "rgb(234, 32, 43)" : "rgba(0, 0, 0, 0.2)",
                                                    transition: "color 0.3s ease"
                                                }}
                                            >
                                                {/* Path hình trái tim (Filled Heart) */}
                                                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"></path>
                                            </svg>
                                        </div>
                                    </div>
                                    <button className="MuiButtonBase-root openImg w-full h-full" type="button" onClick={() => setModalOpen(true)}>
                                        <span style={{ boxSizing: 'border-box', display: 'inline-block', overflow: 'hidden', width: '100%', height: '100%', background: 'none', opacity: 1, border: '0px', margin: '0px', padding: '0px', position: 'relative', maxWidth: '100%' }}>
                                            <img
                                                src={activeImageObj ? activeImageObj.url : ''}
                                                alt={activeImageObj ? activeImageObj.alt : 'Product Image'}
                                                style={{
                                                    position: 'absolute',
                                                    inset: '0px',
                                                    boxSizing: 'border-box',
                                                    padding: '0px',
                                                    border: 'none',
                                                    margin: 'auto',
                                                    display: 'block',
                                                    width: '0px',
                                                    height: '0px',
                                                    minWidth: '100%',
                                                    maxWidth: '100%',
                                                    minHeight: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </span>
                                    </button>

                                </div>
                            </div>
                            {/* ===== THUMBNAIL SLIDER ===== */}
                            <div className="styles_sliderContainer__FwX5B styles_imageSlider__FV2Sv" style={sliderInlineStyles.container}>
                                <div className="styles_wrapperSlider__ChLsw" style={sliderInlineStyles.wrapper}>

                                    {/* ◀ PREV */}
                                    {canSlide && (
                                        <div
                                            className="styles_wrapperLeft__9oUEY"
                                            role="button"
                                            aria-label="prev image"
                                            style={sliderInlineStyles.arrowButton}
                                            onClick={() => sliderRef.current?.slickPrev()}
                                        >
                                            <i className="styles_arrow__BgiX1 styles_left__nHNaN" />
                                        </div>
                                    )}

                                    {/* SLIDER */}
                                    <div style={sliderInlineStyles.sliderViewport}>
                                        <SlickSlider ref={sliderRef as any} key={galleryImages.length} style={sliderInlineStyles.slider} {...sliderSettings}>
                                            {galleryImages.map((img, index) => (
                                                <div key={img.id ?? index} style={sliderInlineStyles.slideItem}>
                                                    <button
                                                        className="MuiButtonBase-root openImg"
                                                        type="button"
                                                        style={sliderInlineStyles.thumbButton}
                                                        onMouseEnter={() => handleThumbnailClick(index)}
                                                        onClick={() => handleThumbnailClick(index)}
                                                    >
                                                        <div
                                                            className={`styles_imageItem__bGCgm ${selectedIndex === index ? "styles_active__wKQN9" : ""}`}
                                                            style={selectedIndex === index ? { ...sliderInlineStyles.thumbItem, ...sliderInlineStyles.thumbItemActive } : sliderInlineStyles.thumbItem}
                                                        >
                                                            <img src={img.url} alt={img.alt} style={sliderInlineStyles.thumbImage} />
                                                        </div>
                                                    </button>
                                                </div>
                                            ))}
                                        </SlickSlider>
                                    </div>

                                    {/* ▶ NEXT */}
                                    {canSlide && (
                                        <div
                                            className="styles_wrapperRight__ryBSR"
                                            role="button"
                                            aria-label="next image"
                                            style={sliderInlineStyles.arrowButton}
                                            onClick={() => sliderRef.current?.slickNext()}
                                        >
                                            <i className="styles_arrow__BgiX1 styles_right__snSLq" />
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                        <div className="styles_buyInfo__7dnyO d-none">
                            <div className="styles_countBuy__Ev3_S">
                                <span
                                    style={{
                                        boxSizing: "border-box",
                                        display: "inline-block",
                                        overflow: "hidden",
                                        width: "initial",
                                        height: "initial",
                                        background: "none",
                                        opacity: 1,
                                        border: 0,
                                        margin: 0,
                                        padding: 0,
                                        position: "relative",
                                        maxWidth: "100%",
                                    }}
                                >
                                    <span
                                        style={{
                                            boxSizing: "border-box",
                                            display: "block",
                                            width: "initial",
                                            height: "initial",
                                            background: "none",
                                            opacity: 1,
                                            border: 0,
                                            margin: 0,
                                            padding: 0,
                                            maxWidth: "100%",
                                        }}
                                    >
                                        <img
                                            alt=""
                                            aria-hidden="true"
                                            src="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20version='1.1'%20width='16'%20height='16'/%3e"
                                            style={{
                                                display: "block",
                                                maxWidth: "100%",
                                                width: "initial",
                                                height: "initial",
                                                background: "none",
                                                opacity: 1,
                                                border: 0,
                                                margin: 0,
                                                padding: 0,
                                            }}
                                        />
                                    </span>

                                    <img
                                        alt="cart_icon"
                                        data-origin-url="https://cdn-web-next.thuocsi.vn/images/icon/shopping-cart.svg"
                                        src="https://cdn-web-next.thuocsi.vn/images/icon/shopping-cart.svg"
                                        decoding="async"
                                        style={{
                                            position: "absolute",
                                            inset: 0,
                                            boxSizing: "border-box",
                                            padding: 0,
                                            border: "none",
                                            margin: "auto",
                                            display: "block",
                                            width: 0,
                                            height: 0,
                                            minWidth: "100%",
                                            maxWidth: "100%",
                                            minHeight: "100%",
                                            maxHeight: "100%",
                                        }}
                                    />
                                </span>

                                <p className="MuiTypography-root styles_numberBuy__3nhbg MuiTypography-body1">
                                    215
                                </p>

                                <p className="MuiTypography-root styles_typography__yL4Xa MuiTypography-body1">
                                    {" "}
                                    Lượt mua trong 24 giờ
                                </p>
                            </div>

                            <p className="MuiTypography-root styles_score__sUEVW MuiTypography-body1" />
                        </div>
                    </div>

                </div>
            </div>

            {/* ================= RIGHT ================= */}
            <div className="MuiGrid-root styles_rightGrid__PGQ7R MuiGrid-item MuiGrid-grid-xs-8">
                {processedData.isFlashSale && processedData.flashSaleEndTime && token && (
                    <HotDealInfo
                        sold={120}
                        progressPercent={65}
                        endTime={processedData.flashSaleEndTime}
                    />
                )}
                <div>
                    <h1 className="MuiTypography-root styles_typographyTitle__RTV69 MuiTypography-h1">
                        <span style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => handleCopy(processedData.code)}>{processedData.code}</span> - {processedData.name}
                    </h1>

                    <p className="MuiTypography-root styles_typographyRight__0cmji MuiTypography-body1">
                        {processedData.shortInfo}
                    </p>
                </div>

                {
                    token && (
                        <div className="styles_rowPrice__giLxB">
                            <div
                                className="styles_priceInfo__66bgZ"
                                ins-init-condition="#W2NsYXNzKj1fcHJpY2VJbmZvX19dLCBbY2xhc3MqPXN0eWxlc19yZWZlcmVuY2VSZXRhaWxQcmljZV9fXQ=="
                            >
                                <div className="relative">
                                    <div
                                        ref={priceRef}
                                        onMouseEnter={handleEnter}
                                        onMouseLeave={handleLeave}
                                        className="cursor-pointer"
                                    >
                                        <p className="MuiTypography-root styles_price__uDwZz flex items-end MuiTypography-body1 gap-[8px]">

                                            {/* PRICE CANVAS */}
                                            <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                                                <div
                                                    className="styles_canvasPrice__vw932"
                                                    style={{ height: "30px" }}
                                                >
                                                    {formatCurrency(processedData.finalEstPrice)}
                                                </div>
                                            </div>

                                            {/* VOUCHER ICON */}
                                            <span style={{ boxSizing: "border-box", display: "inline-block", overflow: "hidden", width: "initial", height: "initial", background: "none", opacity: 1, border: 0, margin: 0, padding: 0, position: "relative", maxWidth: "100%" }}>
                                                <span style={{ boxSizing: "border-box", display: "block", width: "initial", height: "initial", background: "none", opacity: 1, border: 0, margin: 0, padding: 0, maxWidth: "100%" }}><img alt="" aria-hidden="true" src="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20version='1.1'%20width='16'%20height='16'/%3e" style={{ display: "block", maxWidth: "100%", width: "initial", height: "initial", background: "none", opacity: 1, border: 0, margin: 0, padding: 0 }} /></span>
                                                <img alt="icon" src="https://cdn-web-next.thuocsi.vn/icons/productDetail/voucher_icon.svg" decoding="async" style={{ position: "absolute", inset: 0, boxSizing: "border-box", padding: 0, border: "none", margin: "auto", display: "block", width: 0, height: 0, minWidth: "100%", maxWidth: "100%", minHeight: "100%", maxHeight: "100%" }} />
                                            </span>
                                        </p>
                                    </div>

                                    {open && (
                                        <div
                                            role="tooltip"
                                            className="MuiTooltip-popper jss266"
                                            style={{
                                                position: "fixed",
                                                top: "auto",
                                                bottom: "100%",
                                                left: 0,
                                                transform: `translate3d(${pos.left}px, ${pos.top}px, 0) translateX(-50%)`,
                                                willChange: "transform",
                                                zIndex: 1300,
                                            }}
                                        >
                                            <div
                                                className="MuiTooltip-tooltip jss265 MuiTooltip-tooltipPlacementTop"
                                                style={{
                                                    opacity: 1,
                                                    transform: "none",
                                                    transition:
                                                        "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 133ms cubic-bezier(0.4, 0, 0.2, 1)",
                                                }}
                                            >
                                                {/* ================= TOOLTIP CONTENT ================= */}
                                                <div className="text-black flex flex-col gap-[12px] z-50">

                                                    <div className="text-[16px] font-ggsm">
                                                        Chi tiết giá
                                                    </div>

                                                    {/* GIÁ GỐC */}
                                                    <div className="grid grid-cols-3">
                                                        <div className="col-span-2 text-[14px]">Giá gốc</div>
                                                        <div className="col-span-1 text-right text-[14px] font-ggsm">{formatCurrency(processedData.originalPrice)}</div>
                                                    </div>

                                                    <hr className="MuiDivider-root" />

                                                    {/* KHUYẾN MÃI & VOUCHER */}
                                                    {/* {processedData.discountAmount > 0 && (
                                                <div className="grid grid-cols-3">
                                                    <div className="col-span-2 text-[14px]">Giảm giá sốc</div>
                                                    <div className="col-span-1 text-right text-[#F5222D] text-[14px]">- {formatCurrency(processedData.discountAmount)}</div>
                                                </div>
                                            )} */}

                                                    {processedData.discountAmount > 0 && (
                                                        <div className="grid grid-cols-3">
                                                            <div className="col-span-2 text-[14px]">
                                                                {processedData.isFlashSale ? "Flash Sale" : "Khuyến mãi"}
                                                            </div>
                                                            <div className="col-span-1 text-right text-[#F5222D] text-[14px]">
                                                                - {formatCurrency(processedData.discountAmount)}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {processedData.hasVoucher && (
                                                        <div className="grid grid-cols-3">
                                                            <div className="col-span-2 flex flex-col gap-[2px]">
                                                                <div className="text-[14px] leading-5 font-ggsm">Mã giảm giá</div>
                                                                {/* Hiển thị mã code nếu cần */}
                                                                <div className="text-[12px] text-[#8C8C8C]">
                                                                    Áp dụng mã {processedData.voucherCode}
                                                                </div>
                                                            </div>
                                                            <div className="col-span-1 text-right text-[#F5222D] text-[14px]">
                                                                - {formatCurrency(processedData.voucherAmount)}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* MÃ GIẢM GIÁ
                                            <div className="grid grid-cols-3">
                                                <div className="col-span-2 flex flex-col gap-[4px]">
                                                    <div className="text-[14px] leading-5 font-ggsm">
                                                        Mã giảm giá sàn thuocsi
                                                    </div>
                                                    <div className="text-[12px] text-[#8C8C8C]">
                                                        GIẢM 0.1% Cho đơn hàng từ 10.000.000đ
                                                    </div>

                                                    <button
                                                        className="MuiButtonBase-root MuiButton-root MuiButton-text max-w-max p-0"
                                                        type="button"
                                                    >
                                                        <span className="MuiButton-label">
                                                            <div className="text-sm flex items-center text-[#1D39C4]">
                                                                Xem chi tiết&nbsp;&nbsp;
                                                                <svg
                                                                    className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium text-[#1D39C4] !w-[12px] !h-[12px]"
                                                                    viewBox="0 0 24 24"
                                                                >
                                                                    <path d="M11 15h2v2h-2zm0-8h2v6h-2zm.99-5C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2z" />
                                                                </svg>
                                                            </div>
                                                        </span>
                                                    </button>
                                                </div>

                                                <div className="col-span-1 text-right text-[#F5222D] text-[14px]">
                                                    -100đ
                                                </div>
                                            </div> */}

                                                    <hr className="MuiDivider-root" />

                                                    {/* GIÁ CUỐI */}
                                                    <div className="grid grid-cols-3 items-center">
                                                        <div className="col-span-2 text-[14px] font-bold">
                                                            Giá tạm tính <br />
                                                            <span className="text-xs font-normal text-gray-500">(khi áp dụng các mã giảm giá trên)</span>
                                                        </div>
                                                        <div className="col-span-1 text-right text-[18px] font-bold text-[#15A959]">{formatCurrency(processedData.finalEstPrice)}</div>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ORIGINAL PRICE + DISCOUNT */}
                                {processedData.discountAmount > 0 && (
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        <span className="styles_originalPrice__5lefU">
                                            <div className="styles_flex_no_wrap__1vvft MuiBox-root css-0">
                                                <div
                                                    className="styles_canvasPrice__vw932"
                                                    style={{ height: "21px" }}
                                                >
                                                    {formatCurrency(processedData.originalPrice)}
                                                </div>
                                            </div>
                                        </span>

                                        <span className="styles_discountPercent__SrLu1">
                                            -{processedData.discountPercent}%
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* ASSESS PRICE */}
                            <div className="styles_assessPrice__0YRCt">
                                <p className="MuiTypography-root styles_typographyRight__0cmji MuiTypography-body1">
                                    Bạn thấy giá này?
                                </p>

                                {/* BUTTON 1: HỢP LÝ (Value = 1) */}
                                <div className="styles_container__EWHMz">
                                    <span>
                                        <button
                                            className="MuiButtonBase-root MuiIconButton-root styles_root__h_lXN"
                                            type="button"
                                            onClick={() => handleAssess(1)} // Gọi hàm với giá trị 1
                                        >
                                            <span className="MuiIconButton-label">
                                                <svg
                                                    className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-vubbuv"
                                                    focusable="false"
                                                    aria-hidden="true"
                                                    viewBox="0 0 24 24"
                                                    // Đổi màu xanh nếu assessment === 1
                                                    style={{
                                                        color: assessment === 1 ? "#1890ff" : "inherit",
                                                        transition: "color 0.2s"
                                                    }}
                                                >
                                                    <path d="m13.11 5.72-.57 2.89c-.12.59.04 1.2.42 1.66.38.46.94.73 1.54.73H20v1.08L17.43 18H9.34c-.18 0-.34-.16-.34-.34V9.82l4.11-4.1M14 2 7.59 8.41C7.21 8.79 7 9.3 7 9.83v7.83C7 18.95 8.05 20 9.34 20h8.1c.71 0 1.36-.37 1.72-.97l2.67-6.15c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66-.23-.45-.52-.86-.88-1.22L14 2zM4 9H2v11h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1z" />
                                                </svg>
                                            </span>
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </span>

                                    <p
                                        className="MuiTypography-root styles_typography__KesV2 MuiTypography-body1"
                                        style={{ color: assessment === 1 ? "#1890ff" : "inherit", fontWeight: assessment === 1 ? 600 : 400 }}
                                    >
                                        Hợp lí
                                    </p>
                                </div>

                                {/* BUTTON 2: CAO (Value = 2) */}
                                <div className="styles_container__EWHMz">
                                    <span>
                                        <button
                                            className="MuiButtonBase-root MuiIconButton-root styles_root__h_lXN"
                                            type="button"
                                            onClick={() => handleAssess(2)} // Gọi hàm với giá trị 2
                                        >
                                            <span className="MuiIconButton-label">
                                                <svg
                                                    className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-vubbuv"
                                                    focusable="false"
                                                    aria-hidden="true"
                                                    viewBox="0 0 24 24"
                                                    // Đổi màu đỏ nếu assessment === 2
                                                    style={{
                                                        color: assessment === 2 ? "#ff4d4f" : "inherit",
                                                        transition: "color 0.2s"
                                                    }}
                                                >
                                                    <path d="m10.89 18.28.57-2.89c.12-.59-.04-1.2-.42-1.66-.38-.46-.94-.73-1.54-.73H4v-1.08L6.57 6h8.09c.18 0 .34.16.34.34v7.84l-4.11 4.1M10 22l6.41-6.41c.38-.38.59-.89.59-1.42V6.34C17 5.05 15.95 4 14.66 4h-8.1c-.71 0-1.36.37-1.72.97l-2.67 6.15c-.11.25-.17.52-.17.8V13c0 1.1.9 2 2 2h5.5l-.92 4.65c-.05.22-.02.46.08.66.23.45.52.86.88 1.22L10 22zm10-7h2V4h-2c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1z" />
                                                </svg>
                                            </span>
                                            <span className="MuiTouchRipple-root" />
                                        </button>
                                    </span>

                                    <p
                                        className="MuiTypography-root styles_typography__KesV2 MuiTypography-body1"
                                        style={{ color: assessment === 2 ? "#ff4d4f" : "inherit", fontWeight: assessment === 2 ? 600 : 400 }}
                                    >
                                        Cao
                                    </p>
                                </div>
                            </div>
                        </div>
                    )
                }

                {processedData.discountAmount > 0 && token && (<div className="styles_hightLightTag__Wk7k7">
                    {/* TAG 1 */}
                    <Link to="/products/loading?tag=3ABC">
                        <div
                            className="MuiGrid-root tag_tag_containerv2__66m1A tag_normal_tag_container__Twl9u"
                            style={{
                                borderColor: "rgb(242, 244, 250)",
                                color: "var(--primary-color)",
                                backgroundColor: "rgb(242, 244, 250)",
                            }}
                        >
                            <span
                                style={{
                                    boxSizing: "border-box",
                                    display: "inline-block",
                                    overflow: "hidden",
                                    width: "initial",
                                    height: "initial",
                                    background: "none",
                                    opacity: 1,
                                    border: 0,
                                    margin: 0,
                                    padding: 0,
                                    position: "relative",
                                    maxWidth: "100%",
                                }}
                            >
                                <span
                                    style={{
                                        boxSizing: "border-box",
                                        display: "block",
                                        width: "initial",
                                        height: "initial",
                                        background: "none",
                                        opacity: 1,
                                        border: 0,
                                        margin: 0,
                                        padding: 0,
                                        maxWidth: "100%",
                                    }}
                                >
                                    <img
                                        alt=""
                                        aria-hidden="true"
                                        src="data:image/svg+xml,%3csvg%20xmlns=%27http://www.w3.org/2000/svg%27%20version=%271.1%27%20width=%2715%27%20height=%2715%27/%3e"
                                        style={{
                                            display: "block",
                                            maxWidth: "100%",
                                            width: "initial",
                                            height: "initial",
                                            background: "none",
                                            opacity: 1,
                                            border: 0,
                                            margin: 0,
                                            padding: 0,
                                        }}
                                    />
                                </span>

                                <img
                                    data-origin-url="https://thuocsi.vn/icons/tag/ban_chay.svg"
                                    src="https://thuocsi.vn/icons/tag/ban_chay.svg"
                                    decoding="async"
                                    data-nimg="intrinsic"
                                    className="style_icon_svg__xHhyG"
                                    alt=""
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        boxSizing: "border-box",
                                        padding: 0,
                                        border: "none",
                                        margin: "auto",
                                        display: "block",
                                        width: 0,
                                        height: 0,
                                        minWidth: "100%",
                                        maxWidth: "100%",
                                        minHeight: "100%",
                                        maxHeight: "100%",
                                    }}
                                />
                                <noscript />
                            </span>

                            <p
                                className="MuiTypography-root tag_nameTag__RvjhU MuiTypography-body1"
                                style={{ fontFamily: "ggsr" }}
                            >
                                Bán Chạy
                            </p>
                        </div>
                    </Link>

                    {/* TAG 2 */}
                    <Link to="/promotion">
                        <div
                            className="MuiGrid-root tag_tag_container__Ypz6S tag_normal_tag_container__Twl9u"
                            style={{
                                borderColor: "rgb(220, 53, 69)",
                                color: "rgb(255, 255, 255)",
                                backgroundColor: "rgb(220, 53, 69)",
                            }}
                        >
                            <p className="MuiTypography-root tag_nameTag__RvjhU MuiTypography-body1">
                                Khuyến Mãi
                            </p>
                        </div>
                    </Link>
                </div>
                )}

                <div className="max-w-[250px]">
                    <ProductDetailQualityControl productId={product.id} productName={product.name} />

                    {/* <div className="styles_typography__yL4Xa">
                        <p className="MuiTypography-root styles_text_limit__z1qJ0 MuiTypography-body1">Đặt tối đa 311 sản phẩm</p>
                    </div> */}
                </div>

                {
                    token && (
                        <div className="mt-4">
                            <CouponSection vouchers={processedData.vouchersList} />
                        </div>
                    )
                }

                {infoHtml && (
                    <div className="mt-4">
                        <div
                            className="flex flex-col gap-2 mb-6 scroll-mt-[150px]"
                        >
                            {/* Render HTML content */}
                            <div
                                className="[&_*]:m-[revert] [&_iframe]:!max-w-full [&_ul]:[all:revert] [&_ol]:[all:revert] [&_p]:[all:revert] [overflow-wrap:anywhere] [&_table]:inline-block jodit-wysiwyg text-[#333] leading-relaxed"
                                dangerouslySetInnerHTML={{
                                    __html: infoHtml
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <ProductImageModal
                open={modalOpen}
                images={galleryImages.map(i => i.url)}
                activeIndex={selectedIndex}
                onClose={() => setModalOpen(false)}
                onChange={(i) => setSelectedIndex(i)}
            />

        </div>
    )
}
